import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { 
  SquaresFour, 
  Globe, 
  Books, 
  Lightbulb, 
  ChatCircleText, 
  Headset,
  FileText,
  SignOut,
  List,
  X,
  CaretDown,
  Clock,
  Users,
  Code
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getNavItems = (userRole) => {
  const items = [
    { path: "/", icon: SquaresFour, label: "Dashboard" },
    { path: "/sites", icon: Globe, label: "Sites" },
    { path: "/knowledge-base", icon: Books, label: "Vidensbase" },
    { path: "/suggestions", icon: Lightbulb, label: "Forslag" },
    { path: "/handoffs", icon: Headset, label: "Handoffs" },
    { path: "/scheduler", icon: Clock, label: "Planlagt Import" },
    { path: "/widget-preview", icon: ChatCircleText, label: "Chat Widget" },
    { path: "/embed", icon: Code, label: "Embed Kode" },
    { path: "/docs", icon: FileText, label: "API Docs" },
  ];
  
  // Add Users page for admins only
  if (userRole === 'admin') {
    items.push({ path: "/users", icon: Users, label: "Brugere" });
  }
  
  return items;
};

export const Layout = ({ children, user, setUser }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  
  const navItems = getNavItems(user?.role);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      navigate('/login');
      toast.success("Du er nu logget ud");
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen w-64 border-r border-slate-200 bg-white
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-700 flex items-center justify-center">
                <ChatCircleText className="w-5 h-5 text-white" weight="duotone" />
              </div>
              <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                GardinBot
              </span>
            </div>
            <button 
              className="lg:hidden p-1 hover:bg-slate-100 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) => `
                  sidebar-link
                  ${isActive ? 'sidebar-link-active' : ''}
                `}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <item.icon className="w-5 h-5" weight="duotone" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-medium">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <CaretDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <span className={`inline-flex items-center mt-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    user?.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                    user?.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {user?.role === 'admin' ? 'Administrator' : user?.role === 'editor' ? 'Redaktør' : 'Læser'}
                  </span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <SignOut className="w-4 h-4 mr-2" />
                  Log ud
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 border-b border-slate-200 bg-white lg:hidden">
          <button 
            className="p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <List className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex items-center gap-2 ml-3">
            <div className="w-7 h-7 rounded-lg bg-indigo-700 flex items-center justify-center">
              <ChatCircleText className="w-4 h-4 text-white" weight="duotone" />
            </div>
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              GardinBot
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)] lg:min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};
